import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';

import { cn } from '@/lib/shadcn/utils';
import { useUpdateCoordinationProcessStatusMutation } from '@/modules/coordination/mutations/useUpdateCoordinationProcessStatusMutation';
import type {
  CoordinationProcessEventApi,
  CoordinationProcessStatusApi,
  CoordinationProcessStatusHistoryApi,
} from '@/modules/coordination/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Switch } from '@/modules/ui/components/switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface CoordinationControlPanelProps {
  jobId: string;
  coordinationProcessId: string;
  status: CoordinationProcessStatusApi;
  statusHistories: CoordinationProcessStatusHistoryApi[];
  events: CoordinationProcessEventApi[];
  isCoordinationActionAutoApproved: boolean;
}

export const statusBadgeConfig: Record<
  CoordinationProcessStatusApi,
  { label: string; className: string; tooltip: string }
> = {
  Active: {
    label: 'Active',
    className: 'bg-semantic-560/10 text-semantic-560',
    tooltip: 'Coordination is running and will act on the candidate’s job.',
  },
  Paused: {
    label: 'Paused',
    className: 'bg-semantic-540/10 text-semantic-540',
    tooltip:
      'Coordination is paused and won’t take any further action until resumed.',
  },
  Finished: {
    label: 'Closed',
    className: 'bg-muted text-muted-foreground',
    tooltip: 'Coordination is closed for this candidate.',
  },
  Failed: {
    label: 'Closed',
    className: 'bg-muted text-muted-foreground',
    tooltip: 'Coordination is closed for this candidate.',
  },
};

export type CoordinationProcessMode = 'auto' | 'manual';

export const modeBadgeConfig: Record<
  CoordinationProcessMode,
  { label: string; className: string; tooltip: string }
> = {
  auto: {
    label: 'Auto-approve',
    className: 'bg-secondary-500/10 text-secondary-500',
    tooltip:
      'AI-suggested actions are approved automatically, without manual review.',
  },
  manual: {
    label: 'Manual review',
    className: 'bg-muted text-muted-foreground',
    tooltip:
      'AI-suggested actions wait for manual approval before they’re taken.',
  },
};

export function getCoordinationProcessMode(
  isCoordinationActionAutoApproved: boolean,
): CoordinationProcessMode {
  return isCoordinationActionAutoApproved ? 'auto' : 'manual';
}

function getStatusTooltip(
  status: CoordinationProcessStatusApi,
  events: CoordinationProcessEventApi[],
) {
  if (status === 'Failed') return 'Closure reason unavailable.';

  if (status === 'Finished') {
    return events.some((event) => event.type === 'CandidateRejected')
      ? 'Candidate was rejected.'
      : 'Closed manually.';
  }

  return statusBadgeConfig[status].tooltip;
}

export function CoordinationControlPanel({
  jobId,
  coordinationProcessId,
  status,
  statusHistories,
  events,
  isCoordinationActionAutoApproved,
}: CoordinationControlPanelProps) {
  const updateStatusMutation = useUpdateCoordinationProcessStatusMutation(
    jobId,
    coordinationProcessId,
  );

  const { label, className } = statusBadgeConfig[status];
  const statusTooltip = getStatusTooltip(status, events);
  const { label: modeLabel, className: modeClassName } =
    modeBadgeConfig[
      getCoordinationProcessMode(isCoordinationActionAutoApproved)
    ];
  const isToggleable = status === 'Active' || status === 'Paused';
  const closedAt = statusHistories
    .filter((history) => history.toStatus === 'Finished')
    .at(-1)?.createdAt;

  const handleToggle = (checked: boolean) => {
    const nextStatus = checked ? 'Active' : 'Paused';
    updateStatusMutation.mutate(
      { status: nextStatus, isCoordinationActionAutoApproved },
      {
        onSuccess: () => {
          toast.success(
            checked ? 'Coordination resumed' : 'Coordination paused',
          );
        },
        onError: (error) => {
          toast.error(
            `Failed to update coordination status: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  const handleToggleMode = (checked: boolean) => {
    updateStatusMutation.mutate(
      { status, isCoordinationActionAutoApproved: checked },
      {
        onSuccess: () => {
          toast.success(
            checked ? 'Auto-approve enabled' : 'Manual review enabled',
          );
        },
        onError: (error) => {
          toast.error(
            `Failed to update coordination mode: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <div className="flex items-center gap-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge size="sm" className={cn('border-transparent', className)}>
            {label}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{statusTooltip}</TooltipContent>
      </Tooltip>
      {status === 'Finished' && closedAt && (
        <Paragraph variant="caption">
          Closed {format(parseISO(closedAt), 'MMM d, h:mm a')}
        </Paragraph>
      )}
      {isToggleable && (
        <Switch
          checked={status === 'Active'}
          disabled={updateStatusMutation.isPending}
          onCheckedChange={handleToggle}
          aria-label={
            status === 'Active' ? 'Pause coordination' : 'Resume coordination'
          }
        />
      )}
      <Badge size="sm" className={cn('border-transparent', modeClassName)}>
        {modeLabel}
      </Badge>
      {isToggleable && (
        <Switch
          checked={isCoordinationActionAutoApproved}
          disabled={updateStatusMutation.isPending}
          onCheckedChange={handleToggleMode}
          aria-label={
            isCoordinationActionAutoApproved
              ? 'Require manual review'
              : 'Enable auto-approve'
          }
        />
      )}
    </div>
  );
}
